import axios from 'axios';
import { useEffect, useState } from 'react';

import type { AnalyticsDateRange } from '@/types/analytics';
import type { DashboardDataResponse } from '@/types/dashboards';

/**
 * Lazily fetches a saved dashboard's computed widget data on mount and whenever
 * the id or the shared date range changes. Kept off the analytics index payload
 * so the heavy N×M widget queries only run when a dashboard is actually opened.
 */
export function useDashboardData(dashboardId: string, dateRange: AnalyticsDateRange, enabled = true) {
    const [data, setData] = useState<DashboardDataResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const requestKey = `${dashboardId}|${dateRange.from_date}|${dateRange.to_date}|${enabled}`;

    useEffect(() => {
        if (!enabled) {
            return;
        }

        const controller = new AbortController();
        setLoading(true);
        setError(null);

        axios
            .get(route('dashboards.data', dashboardId), {
                params: { from_date: dateRange.from_date, to_date: dateRange.to_date },
                signal: controller.signal,
            })
            .then((response) => setData(response.data as DashboardDataResponse))
            .catch((err) => {
                if (!axios.isCancel(err)) {
                    setError('load_failed');
                }
            })
            .finally(() => setLoading(false));

        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestKey]);

    return { data, loading, error };
}
